/**
 * Native-compatible API utility for Capacitor apps
 * Handles API calls that work on both web and native (iOS/Android)
 */

// PRODUCTION URL - Use Emergent preview URL until Railway is deployed
const FALLBACK_API_URL = 'https://taraanaya-prod-production.up.railway.app';
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || FALLBACK_API_URL;

console.log('[NativeAPI] Using API URL:', API_BASE_URL);

/**
 * Make a native-compatible fetch request
 * @param {string} endpoint - API endpoint (e.g., '/api/location/search')
 * @param {object} options - Fetch options
 * @returns {Promise<Response>}
 */
export const nativeFetch = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultOptions = {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  try {
    console.log(`[NativeFetch] ${mergedOptions.method} ${url}`);
    const response = await fetch(url, mergedOptions);
    console.log(`[NativeFetch] Response status: ${response.status}`);
    return response;
  } catch (error) {
    console.error(`[NativeFetch] Error:`, error);
    throw error;
  }
};

/**
 * GET request helper
 */
export const apiGet = async (endpoint, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const fullEndpoint = queryString ? `${endpoint}?${queryString}` : endpoint;
  
  const response = await nativeFetch(fullEndpoint, { method: 'GET' });
  
  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }
  
  return response.json();
};

/**
 * POST request helper
 */
export const apiPost = async (endpoint, data = {}) => {
  const response = await nativeFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Location search helper - specifically for native apps
 */
export const searchLocation = async (query) => {
  if (!query || query.length < 2) {
    return [];
  }
  
  try {
    const data = await apiGet('/api/location/search', { q: query });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('[searchLocation] Error:', error);
    return [];
  }
};

export default {
  nativeFetch,
  apiGet,
  apiPost,
  searchLocation,
};
